import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { ApiError } from "@/lib/api";
import {
  fetchDocumentCollectionsTree,
  type DocumentCollectionsTreeResponse,
  type ProductCollection,
} from "@/lib/documents";

function collectionSortKey(collection: ProductCollection): string {
  return `${collection.code}\u0000${collection.name}`;
}

function buildForest(data: DocumentCollectionsTreeResponse): {
  collectionsById: Map<string, ProductCollection>;
  childrenByParentId: Map<string, string[]>;
  rootIds: string[];
} {
  const collectionsById = new Map<string, ProductCollection>();
  for (const collection of data.collections) {
    collectionsById.set(collection.id, collection);
  }

  const childIds = new Set<string>();
  const childrenByParentId = new Map<string, string[]>();

  for (const edge of data.part_of_edges) {
    childIds.add(edge.child_collection_id);
    const list = childrenByParentId.get(edge.parent_collection_id) ?? [];
    list.push(edge.child_collection_id);
    childrenByParentId.set(edge.parent_collection_id, list);
  }

  for (const [, children] of childrenByParentId) {
    children.sort((collectionIdA, collectionIdB) => {
      const rowA = collectionsById.get(collectionIdA);
      const rowB = collectionsById.get(collectionIdB);
      if (!rowA || !rowB) return 0;
      return collectionSortKey(rowA).localeCompare(collectionSortKey(rowB), undefined, { sensitivity: "base" });
    });
  }

  const rootIds = data.collections
    .filter((collection) => !childIds.has(collection.id))
    .sort((collectionA, collectionB) =>
      collectionSortKey(collectionA).localeCompare(collectionSortKey(collectionB), undefined, { sensitivity: "base" }),
    )
    .map((collection) => collection.id);

  return { collectionsById, childrenByParentId, rootIds };
}

function CollectionTreeBranch(props: {
  collectionsById: Map<string, ProductCollection>;
  childrenByParentId: Map<string, string[]>;
  rootIds: string[];
}) {
  const { collectionsById, childrenByParentId, rootIds } = props;

  return (
    <ul className="space-y-2 border-l border-slate-200 pl-4">
      {rootIds.map((collectionId) => {
        const collection = collectionsById.get(collectionId);
        if (!collection) return null;
        const childIds = childrenByParentId.get(collectionId) ?? [];
        return (
          <li key={collectionId}>
            <Link
              className="inline-flex flex-wrap items-baseline gap-x-2 text-sm text-brand-800 hover:underline"
              to={`/documents/collections/${collection.id}`}
            >
              <span className="font-mono font-medium">{collection.code}</span>
              <span className="text-slate-900">{collection.name}</span>
            </Link>
            {collection.description ? (
              <p className="mt-0.5 max-w-3xl text-xs text-slate-500">{collection.description}</p>
            ) : null}
            {childIds.length > 0 ? (
              <div className="mt-2">
                <CollectionTreeBranch
                  collectionsById={collectionsById}
                  childrenByParentId={childrenByParentId}
                  rootIds={childIds}
                />
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

export function DocumentCollectionsTreePage() {
  const treeQuery = useQuery<DocumentCollectionsTreeResponse, ApiError>({
    queryKey: ["documents", "collections-tree"],
    queryFn: fetchDocumentCollectionsTree,
  });

  const forest = treeQuery.data ? buildForest(treeQuery.data) : null;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">Product collections</h1>
        <p className="mt-2 max-w-3xl text-sm text-slate-600">
          Browse collections linked by <span className="font-mono text-slate-700">part_of</span> relationships. Root entries are
          collections that are not nested under another. Select a row to open its detail page.
        </p>
      </header>

      {treeQuery.isLoading ? <p className="text-sm text-slate-500">Loading collections…</p> : null}
      {treeQuery.isError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">{treeQuery.error.message}</div>
      ) : null}

      {forest && forest.rootIds.length === 0 && treeQuery.data?.collections.length === 0 ? (
        <p className="text-sm text-slate-500">No product collections yet.</p>
      ) : null}

      {forest && forest.rootIds.length > 0 ? (
        <CollectionTreeBranch
          collectionsById={forest.collectionsById}
          childrenByParentId={forest.childrenByParentId}
          rootIds={forest.rootIds}
        />
      ) : null}

      {forest && forest.rootIds.length === 0 && (treeQuery.data?.collections.length ?? 0) > 0 ? (
        <p className="text-sm text-amber-800">
          Collections exist but none could be placed as tree roots (every collection appears as a child under{" "}
          <span className="font-mono">part_of</span>). Check relationship direction or cycles.
        </p>
      ) : null}
    </div>
  );
}
